<template>
  <div class="-m-3">
    <div class="relative flex items-center">
      <NotebookPen class="pointer-events-none h-5 w-5 mr-2 text-gray-400" aria-hidden="true" />
      <span class="dark:text-white">{{ Record.from(company.record).name }} {{ $t("forecasting notes") }}</span>
      <div class="absolute top-0 right-0 flex flex-wrap gap-2 px-2 pb-2 justify-end">
        <div class="relative flex w-full items-center whitespace-nowrap rounded-full hover:cursor-pointer group">
          <X
            class="text-gray-500 group-hover:bg-gray-100 dark:group-hover:bg-dark-screen-background h-6 w-6 flex-shrink-0 rounded-full"
            @click="$emit('cancel')" />
        </div>
      </div>
    </div>

    <div
      class="mt-2 border-t min-h-[250px] max-h-[500px] overflow-y-auto border-gray-200 dark:border-gray-500 drecordark:border-gray-700 py-6 px-2 text-center text-sm">
      <draggable
        v-model="notes"
        class="min-w-[100%] relative flex flex-row flex-wrap justify-between py-2 px-2 sm:flex-row"
        :component-data="{ type: 'transition-group', name: !drag ? 'flip-list' : null }"
        :group="{ name: 'notes' }"
        v-bind="dragOptions"
        handle=".drag-handle"
        filter=".ignore-drag"
        item-key="id"
        @start="drag = true"
        @end="drag = false">
        <template #item="{ element }">
          <div class="mb-3 py-1 w-full flex items-center p-2 group">
            <div class="w-full flex flex-col">
              <div :class="[focus && selected_note.id == element.id ? 'h-44' : 'h-24']">
                <TextEditor
                  v-if="focus && selected_note.id == element.id"
                  :value="element.description"
                  :show-label="false"
                  :no-border="true"
                  toolbar="essential"
                  name="note"
                  :label="$t('Note')"
                  theme="snow"
                  input-classes="rounded-b-md"
                  @focusin="
                    () => {
                      selected_note = editing_disabled ? null : element
                      focus = editing_disabled ? false : true
                    }
                  "
                  @changed="
                    (e) => {
                      element.description = formatLink(e)
                    }
                  " />
                <div
                  v-else
                  class="w-full h-24 border"
                  :class="[
                    is_confirmation_delete_open && selected_note.id == element.id
                      ? 'border-red-300 rounded-md'
                      : 'border-dark-divider dark:border-white rounded-md'
                  ]">
                  <TextEditor
                    :value="element.description"
                    :disabled="true"
                    :show-label="false"
                    :no-border="true"
                    toolbar="essential"
                    input-classes="rounded-md"
                    placeholder=""
                    name="note"
                    label=""
                    theme="bubble"
                    @click="
                      () => {
                        selected_note = editing_disabled ? null : element
                        focus = editing_disabled ? false : true
                      }
                    " />
                </div>
              </div>
              <div
                class="flex flex-row-reverse justify-start w-full pt-1 italic text-common-grey"
                :class="[focus && selected_note.id == element.id ? 'mt-10' : '']">
                {{ $utils.getUserById(element.user, "full_name") }},
                {{ toDate(element.last_edited) }}
              </div>
            </div>
            <Trash2
              v-if="!is_confirmation_delete_open && !editing_disabled"
              class="text-red-500 opacity-0 group-hover:opacity-100 cursor-pointer w-5 h-5 transition ease-in-out duration-side-menu-transition-duration"
              @click.prevent="
                () => {
                  selected_note = element
                  is_confirmation_delete_open = true
                }
              " />
            <div v-else-if="is_confirmation_delete_open && element.id == selected_note.id" class="flex flex-col items-center ml-2">
              <span class="text-red-500">{{ $t("confirm?") }}</span>
              <div class="flex">
                <CircleCheck class="text-green-500 cursor-pointer w-6 h-6" @click.prevent="deleteNote(element)" />
                <CircleX class="text-red-500 cursor-pointer w-6 h-6" @click.prevent="is_confirmation_delete_open = false" />
              </div>
            </div>
          </div>
        </template>
      </draggable>

      <div v-if="!editing_disabled" class="ml-4 h-24 mt-1 flex items-center" :class="[notes?.length == 0 ? 'max-w-[96%]' : 'max-w-[94%]']">
        <TextEditor
          v-if="is_create_new_note"
          :disabled="editing_disabled"
          :value="description"
          :show-label="false"
          :no-border="true"
          toolbar="essential"
          input-classes="rounded-b-md"
          name="note"
          label="Note"
          theme="snow"
          @focusin="
            () => {
              focus = false
            }
          "
          @changed="
            (e) => {
              description = formatLink(e)
            }
          " />
        <div v-else class="w-full h-24 border rounded-md">
          <TextEditor
            :value="description"
            :disabled="true"
            :show-label="false"
            :no-border="true"
            toolbar="essential"
            input-classes="rounded-md"
            :placeholder="description ? '' : $t('Start typing to create a new note')"
            name="note"
            label=""
            theme="bubble"
            @click="
              () => {
                is_create_new_note = editing_disabled ? false : true
              }
            " />
        </div>
      </div>
    </div>
    <div
      v-if="!editing_disabled"
      class="flex flex-row text-center justify-end gap-2 my-3"
      :class="[notes?.length == 0 ? 'mr-6 -mt-5' : 'mr-14']">
      <button type="button" class="min-w-[90px] cancel-btn" @click="$emit('cancel')">{{ $t("Cancel") }}</button>
      <button type="button" class="min-w-[90px] primary-btn w-fit" @click="save()">{{ $t("Save") }}</button>
    </div>
  </div>
</template>

<script>
import { Record } from "@/models/record"
import dataService from "@/services/data.service"
import draggable from "vuedraggable"
import TextEditor from "@/components/shared/TextEditor.vue"
import { NotebookPen, X, Trash2, CircleCheck, CircleX } from "lucide-vue-next"
import { ALL_EMITTERS } from "@/store/constants/all_emitters"
export default {
  components: {
    draggable,
    TextEditor,
    X,
    Trash2,
    CircleCheck,
    CircleX,
    NotebookPen
  },
  inject: ["canUserEditLayout"],
  props: {
    forecastId: {
      type: String,
      required: true
    },
    company: {
      type: Object,
      required: true
    }
  },
  emits: ["cancel", "done"],
  data() {
    return {
      Record,
      is_create_new_note: false,
      focus: false,
      notes: [],
      selected_note: null,
      is_confirmation_delete_open: false,
      title: new Date(),
      description: null,
      drag: false
    }
  },
  computed: {
    editing_disabled() {
      let editing_disabled_calc = false
      if (this.$route.name == "WorkspaceItem" && this.$store.state.ui.workspace_user_access_scope == "read") {
        editing_disabled_calc = true
      }

      if (this.$route.name == "Dashboard" && this.$route.params.page) {
        const current_template = this.$store.state.portfolio.shared_layouts.find((l) => l.id == this.$route.params.page)
        if (!this.canUserEditLayout(this.$store.state.auth.user, current_template)) {
          editing_disabled_calc = true
        }
      }

      return editing_disabled_calc
    },
    dragOptions() {
      return {
        animation: 200,
        group: "notes",
        disabled: false,
        ghostClass: "ghost",
        chosenClass: "chosen"
      }
    }
  },
  created() {
    dataService.getForecastNotes(this.forecastId, this.company.id).then((result) => {
      this.notes = result.data.results
    })
  },
  methods: {
    formatLink(str) {
      return str.replace(/href="(?!http)/, 'href="http://')
    },
    save() {
      if (this.description) {
        dataService
          .addNoteToForecast(this.forecastId, this.company.id, {
            title: this.title,
            description: this.description
          })
          .then((res) => {
            this.notes.push(res.data)
            this.$emitter.emit(ALL_EMITTERS.UPDATE_COUNT_NOTES_IN_DASHBOARD_FORECASTING_TABLE, {
              id: this.company.id,
              count: this.notes.length
            })
            this.$emitter.emit(ALL_EMITTERS.UPDATE_COUNT_NOTES_IN_OBJECT_FORECAST, {
              id: this.company.id,
              count: this.notes.length
            })
          })
      }
      this.notes.map((note) => {
        dataService.updateForecastNote(this.forecastId, this.company.id, note.id, {
          title: this.title,
          description: note.description
        })
      })
      this.$emit("done")
    },
    deleteNote(note) {
      dataService.deleteForecastNote(this.forecastId, this.company.id, note.id).then(() => {
        let index = this.notes.findIndex((n) => n.id == note.id)
        this.notes.splice(index, 1)

        this.$emitter.emit(ALL_EMITTERS.UPDATE_COUNT_NOTES_IN_DASHBOARD_FORECASTING_TABLE, {
          id: this.company.id,
          count: this.notes.length
        })
        this.$emitter.emit(ALL_EMITTERS.UPDATE_COUNT_NOTES_IN_OBJECT_FORECAST, {
          id: this.company.id,
          count: this.notes.length
        })
        this.is_confirmation_delete_open = false
      })
    },
    toDate(date_string) {
      let date = new Date(date_string)
      const options = { weekday: "long", year: "numeric", month: "long", day: "numeric" }
      let localStringDate = date.toLocaleDateString(undefined, options)
      return localStringDate.substr(localStringDate.indexOf(",") + 1)
    }
  }
}
</script>
